import { useEffect, useRef, useState } from "react";
import { Panel } from "./ui";
import { useBakeryStore } from "../game/bakeryStore";
import { sfxClick, unlockAudioOnce } from "../game/music";
import { apiFetch } from "../auth/api";

// CPS tester: taps go through the normal click path (store.click + click
// SFX) so each tap still bakes cookies, while the card measures tap rate
// as a rolling average over the last 5s plus the session peak.
export default function CpsTester() {
  const store = useBakeryStore();
  const [taps, setTaps] = useState<number[]>([]);
  const [total, setTotal] = useState(0);
  const [peak, setPeak] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const peakRef = useRef(peak);
  const cpsRef = useRef(0);
  const guildId = store.snapshot?.guild?.id ?? "";

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(t);
  }, []);

  const recent = taps.filter((t) => t > now - 5000);
  const count = recent.length;
  const spanSec = count > 1 ? Math.max(0.001, (now - (recent[0] ?? now)) / 1000) : 1;
  const divisor = count === 0 ? 1 : Math.min(5, Math.max(1, spanSec));
  const cps = count === 0 ? 0 : count / divisor;

  useEffect(() => {
    setPeak((p) => (cps > p ? cps : p));
  }, [cps]);

  useEffect(() => {
    peakRef.current = peak;
  }, [peak]);

  useEffect(() => {
    cpsRef.current = cps;
  }, [cps]);

  useEffect(() => {
    if (guildId === "") return;
    let lastSent = 0;
    const id = window.setInterval(() => {
      const sendVal = Math.max(peakRef.current, cpsRef.current);
      if (!Number.isFinite(sendVal) || sendVal <= 0) return;
      if (Math.abs(sendVal - lastSent) < 0.05) return;
      lastSent = sendVal;
      void apiFetch("/api/bakery/" + encodeURIComponent(guildId) + "/tester-cps", { method: "POST", body: { cps: sendVal } }).catch(() => undefined);
    }, 1000);
    return () => window.clearInterval(id);
  }, [guildId]);

  // Drop fully-expired taps so the array stays bounded while idle.
  useEffect(() => {
    if (taps.length > 0 && (taps[taps.length - 1] ?? 0) <= now - 5000 && count === 0) {
      setTaps([]);
    }
  }, [now, taps, count]);

  const handleTap = () => {
    const at = Date.now();
    setTaps((prev) => [...prev.filter((t) => t > at - 5000), at]);
    setTotal((v) => v + 1);
    // Normal click path: bakes cookies (optimistic + server) with click SFX.
    store.click(0.5 + (Math.random() - 0.5) * 0.1, 0.52 + (Math.random() - 0.5) * 0.1);
    unlockAudioOnce();
    sfxClick();
  };

  const reset = () => {
    setTaps([]);
    setTotal(0);
    setPeak(0);
    setNow(Date.now());
  };

  return (
    <Panel>
      <h3 className="cb-sec on-light">👆 CPS Tester</h3>
      <p className="dim small" style={{ margin: "0 0 8px" }}>
        Mash the pad — every tap bakes cookies through the normal click path (+{store.cookiesPerClick} 🍪/click).
        {!store.connected && " Reconnect to earn cookies."}
      </p>
      <button
        type="button"
        className="cb-buy"
        onClick={handleTap}
        aria-label="CPS test pad, tap to bake cookies"
        style={{ width: "100%", minHeight: 76, fontSize: 30, fontWeight: 800 }}
      >
        🍪 TAP
      </button>
      <div role="status" aria-live="polite" style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
        <span><b style={{ fontSize: 22 }}>{cps.toFixed(1)}</b> <small className="dim">clicks/sec (5s rolling)</small></span>
        <span><b style={{ fontSize: 22 }}>{peak.toFixed(1)}</b> <small className="dim">peak</small></span>
        <span><b style={{ fontSize: 22 }}>{total}</b> <small className="dim">total taps</small></span>
      </div>
      <div className="cb-row" style={{ marginTop: 8 }}>
        <button type="button" className="cb-buy ghost" onClick={reset} aria-label="Reset CPS tester">
          Reset
        </button>
      </div>
    </Panel>
  );
}
